import { Card, Button } from '@suscri/ui';
import { ArrowLeft, Shield, UserPlus, Edit, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { trpc } from '../../lib/trpc';
import type { RouterOutputs } from '../../lib/trpc';

type AdminUser = RouterOutputs['admin']['listUsers']['users'][number];

export default function UsersPage() {
  const navigate = useNavigate();
  const [token] = useState<string | null>(() =>
    localStorage.getItem('adminToken')
  );
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [showRoles, setShowRoles] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = trpc.admin.listUsers.useQuery(
    { token: token || '' },
    { enabled: Boolean(token) }
  );

  useEffect(() => {
    if (token) {
      refetch();
    }
  }, [token, refetch]);

  const users = useMemo(() => data?.users ?? [], [data]);

  const { data: rolesData, refetch: refetchRoles } =
    trpc.admin.listRoles.useQuery(
      { token: token || '' },
      { enabled: Boolean(token) }
    );
  const roles = useMemo(() => rolesData?.roles ?? [], [rolesData]);

  const createUserMutation = trpc.admin.createUser.useMutation({
    onSuccess: () => {
      refetch();
      setShowCreateForm(false);
    },
  });
  const updateUserMutation = trpc.admin.updateUser.useMutation({
    onSuccess: () => {
      refetch();
      setEditingUser(null);
    },
  });
  const createRoleMutation = trpc.admin.createRole.useMutation({
    onSuccess: () => {
      refetchRoles();
    },
  });
  const assignRoleMutation = trpc.admin.assignRole.useMutation({
    onSuccess: () => {
      refetch();
      refetchRoles();
    },
  });
  const removeRoleMutation = trpc.admin.removeRole.useMutation({
    onSuccess: () => {
      refetch();
      refetchRoles();
    },
  });

  if (!token) {
    return (
      <div className="p-6">
        No autorizado. Inicia sesión como administrador.
      </div>
    );
  }

  return (
    <div className="app-shell min-h-screen">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-4 py-5 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver
              </Button>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Usuarios y Permisos
                </h1>
                <p className="text-sm text-gray-600">
                  Gestión de usuarios y roles del sistema
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg"
            >
              <UserPlus className="h-4 w-4" />
              Crear Usuario
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Formulario Crear Usuario */}
        {showCreateForm && (
          <Card className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl p-6 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center shadow-lg">
                  <UserPlus className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Crear Nuevo Usuario
                </h3>
              </div>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  createUserMutation.mutate({
                    token: token,
                    email: formData.get('email') as string,
                    password: formData.get('password') as string,
                    name: (formData.get('name') as string) || undefined,
                  });
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="usuario@ejemplo.com"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contraseña
                    </label>
                    <input
                      name="password"
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre
                    </label>
                    <input
                      name="name"
                      type="text"
                      placeholder="Nombre completo"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={createUserMutation.isPending}
                    className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg"
                  >
                    {createUserMutation.isPending
                      ? 'Creando...'
                      : 'Crear Usuario'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                    className="hover:bg-gray-50"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        )}

        {/* Formulario Editar Usuario */}
        {editingUser && (
          <Card className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl p-6 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center shadow-lg">
                  <Edit className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Editar Usuario
                </h3>
              </div>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  updateUserMutation.mutate({
                    token: token,
                    id: editingUser.id,
                    name: (formData.get('name') as string) || undefined,
                    locale: (formData.get('locale') as string) || undefined,
                    currency: (formData.get('currency') as string) || undefined,
                    theme: (formData.get('theme') as string) || undefined,
                  });
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre
                    </label>
                    <input
                      name="name"
                      type="text"
                      placeholder="Nombre completo"
                      defaultValue={editingUser.name ?? ''}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Idioma
                    </label>
                    <select
                      name="locale"
                      defaultValue={editingUser.locale}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="es">Español</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Moneda
                    </label>
                    <select
                      name="currency"
                      defaultValue={editingUser.currency}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tema
                    </label>
                    <select
                      name="theme"
                      defaultValue={editingUser.theme}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="system">Sistema</option>
                      <option value="light">Claro</option>
                      <option value="dark">Oscuro</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={updateUserMutation.isPending}
                    className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white shadow-lg"
                  >
                    {updateUserMutation.isPending
                      ? 'Guardando...'
                      : 'Guardar Cambios'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingUser(null)}
                    className="hover:bg-gray-50"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        )}
        {/* Tabla de Usuarios */}
        <Card className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl p-0">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Lista de Usuarios
                </h3>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Idioma
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Moneda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Creado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading && (
                    <tr>
                      <td
                        className="px-6 py-4 text-center text-gray-500"
                        colSpan={6}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                          Cargando usuarios...
                        </div>
                      </td>
                    </tr>
                  )}
                  {isError && (
                    <tr>
                      <td
                        className="px-6 py-4 text-center text-red-600"
                        colSpan={6}
                      >
                        Error cargando usuarios
                      </td>
                    </tr>
                  )}
                  {!isLoading && !isError && users.length === 0 && (
                    <tr>
                      <td
                        className="px-6 py-4 text-center text-gray-500"
                        colSpan={6}
                      >
                        No hay usuarios registrados
                      </td>
                    </tr>
                  )}
                  {users.map(u => (
                    <tr
                      key={u.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {u.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {u.name ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {u.locale}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {u.currency}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {new Date(u.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingUser(u)}
                            className="hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowRoles(u.id)}
                            className="hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700"
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            Roles
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        {/* Modal Gestión de Roles */}
        {showRoles && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border-0 shadow-2xl rounded-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center shadow-lg">
                        <Shield className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        Gestión de Roles
                      </h3>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setShowRoles(null)}
                      className="hover:bg-gray-50"
                    >
                      Cerrar
                    </Button>
                  </div>

                  {/* Crear Rol */}
                  <div className="mb-6 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded flex items-center justify-center">
                        <UserPlus className="h-3 w-3 text-white" />
                      </div>
                      Crear Nuevo Rol
                    </h4>
                    <form
                      onSubmit={e => {
                        e.preventDefault();
                        const formData = new FormData(
                          e.target as HTMLFormElement
                        );
                        createRoleMutation.mutate({
                          token: token,
                          name: formData.get('roleName') as string,
                          description:
                            (formData.get('roleDesc') as string) || undefined,
                        });
                      }}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nombre del Rol
                          </label>
                          <input
                            name="roleName"
                            placeholder="Ej: Administrador"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Descripción
                          </label>
                          <input
                            name="roleDesc"
                            placeholder="Descripción del rol"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      <Button
                        type="submit"
                        disabled={createRoleMutation.isPending}
                        className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg"
                      >
                        {createRoleMutation.isPending
                          ? 'Creando...'
                          : 'Crear Rol'}
                      </Button>
                    </form>
                  </div>

                  {/* Lista de Roles */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-cyan-600 rounded flex items-center justify-center">
                        <Users className="h-3 w-3 text-white" />
                      </div>
                      Roles Disponibles
                    </h4>
                    <div className="space-y-3">
                      {roles.map(role => (
                        <div
                          key={role.id}
                          className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                        >
                          <div>
                            <span className="font-medium text-gray-900">
                              {role.name}
                            </span>
                            {role.description && (
                              <span className="text-sm text-gray-600 ml-2">
                                - {role.description}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                assignRoleMutation.mutate({
                                  token: token,
                                  userId: showRoles,
                                  roleId: role.id,
                                });
                              }}
                              className="hover:bg-green-50 hover:border-green-300 hover:text-green-700"
                            >
                              Asignar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                removeRoleMutation.mutate({
                                  token: token,
                                  userId: showRoles,
                                  roleId: role.id,
                                });
                              }}
                              className="hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                            >
                              Remover
                            </Button>
                          </div>
                        </div>
                      ))}
                      {roles.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          No hay roles creados aún
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
